/**
 * NFA pickers — every value is chosen, never typed.
 * Searchable combobox, WBS element picker, money stepper with Lakh/Crore
 * presets, numeric stepper, segmented toggle and a people multi-picker.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Minus, Plus, Search, X } from 'lucide-react';
import api from '@/lib/api';
import { money } from './nfaTheme';

export const Combo = ({ T, value, onChange, options, placeholder = 'Select…',
                        testId, searchPlaceholder = 'Type to filter…', disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState(null);
  const box = useRef(null);
  const menu = useRef(null);

  // The menu is portalled to <body> so it can never be clipped by a panel's
  // overflow-hidden or by a parent with a transform.
  const place = useCallback(() => {
    if (!box.current) return;
    const r = box.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const up = below < 220 && r.top > below;
    setPos({
      left: r.left, width: r.width, up,
      top: up ? undefined : r.bottom + 4,
      bottom: up ? window.innerHeight - r.top + 4 : undefined,
      maxH: Math.max(170, Math.min(340, (up ? r.top : below) - 14)),
    });
  }, []);

  useEffect(() => {
    const away = (e) => {
      if (box.current?.contains(e.target) || menu.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return options;
    return options.filter((o) => `${o.label} ${o.sub || ''} ${o.value}`.toLowerCase().includes(n));
  }, [q, options]);
  const current = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={box}>
      <button type="button" disabled={disabled} data-testid={testId}
        onClick={() => { setOpen((v) => !v); setQ(''); }}
        className="w-full flex items-center gap-2 text-[12px] rounded-md px-2.5 py-1.5 text-left disabled:opacity-60"
        style={{ background: T.isDark ? T.surfaceAlt : '#FFFFFF',
                 border: `1px solid ${open ? T.primary : T.border}`, color: T.ink }}>
        <span className="truncate flex-1">
          {current ? current.label : <span style={{ color: T.muted }}>{placeholder}</span>}
        </span>
        {current?.sub && <span className="text-[10px] shrink-0" style={{ color: T.muted }}>{current.sub}</span>}
        <ChevronDown className="w-3.5 h-3.5 shrink-0" style={{ color: T.muted }} />
      </button>
      {open && pos && createPortal((
        <div ref={menu} className="fixed rounded-lg overflow-hidden shadow-2xl"
          style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: Math.max(pos.width, 220),
                   zIndex: 10000, background: T.surface, border: `1px solid ${T.borderStrong}` }}
          data-testid={testId ? `${testId}-menu` : undefined}>
          <div className="flex items-center gap-2 px-2.5 py-2" style={{ borderBottom: `1px solid ${T.border}` }}>
            <Search className="w-3.5 h-3.5" style={{ color: T.muted }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined}
              className="flex-1 bg-transparent outline-none text-[12px]" style={{ color: T.ink }} />
            <span className="text-[10px] tabular-nums" style={{ color: T.muted }}>{filtered.length}</span>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: pos.maxH }}>
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-[11px] text-center" style={{ color: T.muted }}>No match</div>
            )}
            {filtered.map((o) => (
              <button type="button" key={o.value} onClick={() => { onChange(o.value, o); setOpen(false); }}
                data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[11.5px] hover:opacity-80"
                style={{ background: o.value === value ? T.tint(T.primary) : 'transparent',
                         paddingLeft: 10 + (o.indent || 0) * 12 }}>
                {o.value === value ? <Check className="w-3 h-3 shrink-0" style={{ color: T.primary }} />
                  : <span className="w-3 shrink-0" />}
                <span className="truncate flex-1" style={{ color: T.ink }}>{o.label}</span>
                {o.badge && (
                  <span className="text-[9px] px-1.5 rounded-full shrink-0"
                    style={{ background: T.tint(T.green), color: T.green }}>{o.badge}</span>
                )}
                {o.sub && <span className="text-[10px] shrink-0 tabular-nums" style={{ color: T.muted }}>{o.sub}</span>}
              </button>
            ))}
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export const WbsPicker = ({ T, projectId, value, onChange, testId = 'nfa-wbs-picker' }) => {
  const [elements, setElements] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/nfa/wbs-elements?project_id=${projectId}&limit=1000`)
      .then((r) => setElements(r.data.elements || []))
      .catch(() => setElements([]));
  }, [projectId]);
  const options = useMemo(() => elements.map((e) => ({
    value: e.code,
    label: `${e.code}  ·  ${e.name || 'Unnamed element'}`,
    sub: e.budget ? money(e.budget) : null,
    badge: e.usedBefore ? 'used' : null,
    indent: Math.min(e.level || 0, 5),
  })), [elements]);
  const picked = elements.find((e) => e.code === value);
  return (
    <div className="space-y-1">
      <Combo T={T} value={value} onChange={onChange} options={options} testId={testId}
        placeholder={elements.length ? 'Pick a WBS element…' : 'Loading WBS elements…'}
        searchPlaceholder="Search by code or name…" />
      {picked && (
        <p className="text-[10px]" style={{ color: T.muted }} data-testid={`${testId}-detail`}>
          {picked.name} · level {picked.level} · {picked.isSummary ? 'summary element' : 'control account'}
          {picked.budget ? ` · budget ${money(picked.budget)}` : ''}
          {picked.incurred ? ` · incurred ${money(picked.incurred)}` : ''}
        </p>
      )}
    </div>
  );
};

const MONEY_STEPS = [100000, 500000, 2500000, 10000000, 50000000];
const MONEY_PRESETS = [
  ['₹25 L', 2500000], ['₹1 Cr', 10000000], ['₹5 Cr', 50000000],
  ['₹15 Cr', 150000000], ['₹50 Cr', 500000000], ['₹100 Cr', 1000000000],
];

export const MoneyStepper = ({ T, value, onChange, testId, allowInfinite = false, compact = false }) => {
  const n = Number(value || 0);
  const [stepIdx, setStepIdx] = useState(1);
  const step = MONEY_STEPS[stepIdx];
  const bump = (dir) => onChange(Math.max(0, n + dir * step));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => bump(-1)} data-testid={testId ? `${testId}-minus` : undefined}
          className="w-6 h-6 rounded grid place-items-center shrink-0"
          style={{ background: T.tint(T.primary), color: T.primary }}><Minus className="w-3 h-3" /></button>
        <div className="flex-1 text-center text-[13px] font-semibold tabular-nums rounded-md py-1"
          style={{ background: T.isDark ? T.surfaceAlt : '#FFFFFF', border: `1px solid ${T.border}`, color: T.ink }}>
          {allowInfinite && n === 0 ? '∞ no ceiling' : money(n)}
        </div>
        <button type="button" onClick={() => bump(1)} data-testid={testId ? `${testId}-plus` : undefined}
          className="w-6 h-6 rounded grid place-items-center shrink-0"
          style={{ background: T.tint(T.primary), color: T.primary }}><Plus className="w-3 h-3" /></button>
      </div>
      <input type="range" min={0} max={1000000000} step={step} value={Math.min(n, 1000000000)}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full h-1 accent-current"
        style={{ accentColor: T.primary }} data-testid={testId ? `${testId}-slider` : undefined} />
      <div className="flex items-center gap-1 flex-wrap">
        {(compact ? MONEY_PRESETS.slice(0, 3) : MONEY_PRESETS).map(([label, v]) => (
          <button type="button" key={label} onClick={() => onChange(v)}
            data-testid={testId ? `${testId}-preset-${v}` : undefined}
            className="text-[9.5px] px-1.5 py-0.5 rounded-full"
            style={{ background: n === v ? T.primary : T.tint(T.primary),
                     color: n === v ? '#fff' : T.primary }}>{label}</button>
        ))}
        <button type="button" onClick={() => setStepIdx((i) => (i + 1) % MONEY_STEPS.length)}
          className="text-[9.5px] px-1.5 py-0.5 rounded-full ml-auto"
          data-testid={testId ? `${testId}-step` : undefined}
          style={{ background: T.tint(T.accent), color: T.accent }}>
          {compact ? `±${money(step)}` : `step ${money(step)}`}
        </button>
      </div>
    </div>
  );
};

export const Stepper = ({ T, value, onChange, min = 1, max = 60, suffix = '', testId }) => {
  const n = Number(value || 0);
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <button type="button" onClick={() => onChange(Math.max(min, n - 1))}
        data-testid={testId ? `${testId}-minus` : undefined}
        className="w-6 h-6 rounded grid place-items-center shrink-0"
        style={{ background: T.tint(T.primary), color: T.primary }}><Minus className="w-3 h-3" /></button>
      <span className="flex-1 text-center text-[12px] font-semibold tabular-nums rounded-md py-1"
        style={{ background: T.isDark ? T.surfaceAlt : '#FFFFFF', border: `1px solid ${T.border}`, color: T.ink }}>
        {n}{suffix}
      </span>
      <button type="button" onClick={() => onChange(Math.min(max, n + 1))}
        data-testid={testId ? `${testId}-plus` : undefined}
        className="w-6 h-6 rounded grid place-items-center shrink-0"
        style={{ background: T.tint(T.primary), color: T.primary }}><Plus className="w-3 h-3" /></button>
    </div>
  );
};

export const SegToggle = ({ T, value, onChange, options, testId }) => (
  <div className="inline-flex rounded-md overflow-hidden" data-testid={testId}
    style={{ border: `1px solid ${T.border}` }}>
    {options.map((o) => (
      <button type="button" key={o.value} onClick={() => onChange(o.value)}
        data-testid={testId ? `${testId}-${o.value}` : undefined}
        className="px-2.5 py-1 text-[10.5px] font-semibold whitespace-nowrap"
        style={{ background: value === o.value ? (o.tone || T.primary) : 'transparent',
                 color: value === o.value ? '#fff' : T.body }}>{o.label}</button>
    ))}
  </div>
);

export const UserMultiPicker = ({ T, projectId, value = [], onChange, testId = 'nfa-user-picker' }) => {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get(`/nfa/directory/users${projectId ? `?project_id=${projectId}` : ''}`)
      .then((r) => setUsers(r.data.users || [])).catch(() => setUsers([]));
  }, [projectId]);
  const byId = useMemo(() => Object.fromEntries(users.map((u) => [u.user_id, u])), [users]);
  const options = users
    .filter((u) => !value.includes(u.user_id))
    .map((u) => ({ value: u.user_id, label: u.name || u.email, sub: u.email }));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        {value.length === 0 && (
          <span className="text-[10.5px]" style={{ color: T.muted }}>— unassigned —</span>
        )}
        {value.map((uid) => (
          <span key={uid} className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full"
            data-testid={`${testId}-chip-${uid}`}
            style={{ background: T.tint(T.primary), color: T.primary }}>
            {byId[uid]?.name || uid}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== uid))}
              data-testid={`${testId}-remove-${uid}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <Combo T={T} value="" onChange={(uid) => onChange([...value, uid])} options={options}
        testId={testId} placeholder="Add a person…" searchPlaceholder="Search people…" />
    </div>
  );
};

export const LockedValue = ({ T, children, hint, testId }) => (
  <div className="space-y-1">
    <div className="w-full text-[12px] rounded-md px-2.5 py-1.5 flex items-center gap-2"
      data-testid={testId}
      style={{ background: T.isDark ? T.page : '#F4F6FA', border: `1px dashed ${T.border}`, color: T.ink }}>
      <span className="truncate">{children}</span>
      <span className="ml-auto text-[9px] uppercase tracking-wider shrink-0" style={{ color: T.muted }}>locked</span>
    </div>
    {hint && <p className="text-[9.5px]" style={{ color: T.muted }}>{hint}</p>}
  </div>
);
